import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  HomeIcon, 
  MapPinIcon, 
  BoltIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
  ClockIcon,
  IdentificationIcon,
  UsersIcon,
  Bars3Icon,
  XMarkIcon,
  MapIcon,
  BellIcon
} from '@heroicons/react/24/outline';

interface LayoutProps {
  children: React.ReactNode;
  isAdmin: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, isAdmin }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const handleSignOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const adminNavigationItems = [
    { name: t('layout.navigation.dashboard'), path: '/admin/dashboard', icon: HomeIcon },
    { name: t('layout.navigation.operators'), path: '/admin/operators', icon: UserGroupIcon },
    { name: t('layout.navigation.vendors'), path: '/admin/vendors', icon: BoltIcon },
    { name: t('layout.navigation.areas'), path: '/admin/areas', icon: MapIcon },
    { name: t('layout.navigation.locations'), path: '/admin/locations', icon: MapPinIcon },
    { name: t('layout.navigation.pendingImages'), path: '/admin/pending-images', icon: MapPinIcon },
    { name: t('layout.navigation.chargePoints'), path: '/admin/charge-points', icon: BoltIcon },
    { name: t('layout.navigation.transactions'), path: '/admin/transactions', icon: ClockIcon },
    { name: t('layout.navigation.users'), path: '/admin/users', icon: UsersIcon },
    { name: t('layout.navigation.rfidTags'), path: '/admin/rfid-tags', icon: IdentificationIcon },
    { name: t('layout.navigation.settings'), path: '/admin/settings', icon: Cog6ToothIcon },
    { name: t('layout.navigation.notifications'), path: '/admin/notifications', icon: BellIcon },
  ];

  const operatorNavigationItems = [
    { name: t('layout.navigation.dashboard'), path: '/operator/dashboard', icon: HomeIcon },
    { name: t('layout.navigation.locations'), path: '/operator/locations', icon: MapPinIcon },
    { name: t('layout.navigation.chargePoints'), path: '/operator/charge-points', icon: BoltIcon },
    { name: t('layout.navigation.transactions'), path: '/operator/transactions', icon: ClockIcon },
    { name: t('layout.navigation.users'), path: '/operator/users', icon: UsersIcon },
    { name: t('layout.navigation.rfidTags'), path: '/operator/rfid-tags', icon: IdentificationIcon },
    { name: t('layout.navigation.settings'), path: '/operator/settings', icon: Cog6ToothIcon },
  ];

  const navigationItems = isAdmin ? adminNavigationItems : operatorNavigationItems;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-0 left-0 m-4 z-50">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none"
        >
          {isSidebarOpen ? (
            <XMarkIcon className="h-6 w-6" />
          ) : (
            <Bars3Icon className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-40 w-64 bg-gray-800 transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0 lg:flex lg:flex-col
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex h-16 items-center justify-center bg-gray-900">
          <h1 className="text-white text-xl font-bold">{t('layout.title')}</h1>
        </div>
        <nav className="mt-5 px-2 flex flex-col h-[calc(100%-4rem)]">
          <div className="flex-grow">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsSidebarOpen(false)}
                  className={`group flex items-center px-2 py-2 text-base font-medium rounded-md mb-2
                    ${isActiveRoute(item.path)
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                >
                  <Icon
                    className={`mr-4 h-6 w-6 flex-shrink-0
                      ${isActiveRoute(item.path)
                        ? 'text-white'
                        : 'text-gray-400 group-hover:text-white'
                      }`}
                    aria-hidden="true"
                  />
                  {item.name}
                </Link>
              );
            })}
          </div>

          <button
            onClick={handleSignOut}
            className="group flex items-center px-2 py-2 text-base font-medium rounded-md mb-4 text-gray-300 hover:bg-gray-700 hover:text-white"
          >
            <ArrowRightOnRectangleIcon
              className="mr-4 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-white"
              aria-hidden="true"
            />
            {t('layout.actions.signOut')}
          </button>
        </nav>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 py-6 px-4 sm:px-6 lg:px-8">
          {children}
        </main>

        <footer className="bg-gray-800 text-white p-4 text-center">
          <p>{t('layout.footer.copyright')}</p>
        </footer>
      </div>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout; 